/**
 * EL CUADERNO DE UN TITULAR Y UN AÑO, LISTO PARA DESCARGAR (CQ4a).
 *
 *   buildCuaderno(entrada, borrador, legal) → { data, huecos, preguntas, cobertura, revision, … }
 *
 * Junta las dos mitades:
 *   - los HECHOS, que rellena el motor puro (`construirCuaderno`, CQ2) desde los
 *     partes, las fincas, la maquinaria, los carnés y los datos legales;
 *   - las DECISIONES DEL DOCUMENTO, que guarda el borrador (`cuaderno_drafts`,
 *     D16): «NO aplica» del 3.2 al 3.5, periodo y análisis contestados, fecha de
 *     apertura, nº de orden de parcela forzados, eficacia por defecto confirmada
 *     en bloque (D17) y las líneas que NO son fitosanitarios fuera, con su motivo.
 *
 * CU9 (28-09-2026, «el documento es de la persona»): el borrador también guarda
 * lo que la persona quiere que diga el documento de cada línea (EDICIONES) y las
 * aplicaciones que añade (LÍNEAS AÑADIDAS). Aquí las añadidas se convierten en
 * partes y líneas SINTÉTICAS de la entrada (una por nave, id `anadida:<id>:<nave>`,
 * con `anadida: true`), para que el motor les dé parcela y fila como a cualquiera,
 * y las ediciones pasan al motor, que las pone encima con origen «usuario». Nada
 * de eso existe en `spray_logs` ni en `spray_log_entries`: `entrada` (la que se
 * devuelve) es la EFECTIVA, y la guarda de salida mira esa.
 *
 * Los números que salen de los partes (dosis, superficies, nº de registro) los
 * pone SOLO el motor: aquí no se toca ninguno. Lo que sí se retoca después del motor (fecha de apertura,
 * nº de orden forzados, origen de la eficacia confirmada) no cambia ninguna cifra
 * y queda marcado como «usuario» en la hoja de revisión. La guarda de salida
 * (`guardaSalida.ts`) lo vuelve a comprobar antes de generar.
 *
 * PURO: sin red. Quien carga es `loader.ts`.
 */
import { construirCuaderno, type ResultadoCuaderno } from "./motor";
import type { EntradaCuaderno, LineaEntrada, ParteEntrada } from "./entrada";
import type { DatosLegalesRow } from "./datosLegales";
import type { Hueco, Origen, Pregunta } from "./modeloOficial";
import { planDeParcelas } from "./parcelOrder";
import type { MetaFila31 } from "./rows31";
import type { CuadernoDataInput, ParcelasRef } from "./types";
import { leerBorrador, type BorradorCuaderno, type DecisionesBorrador, type EdicionLinea, type LineaNueva } from "./borrador";
import { hojaDeRevision, type HojaRevision } from "./revision";

/** Una parcela del 2.1 con su CLAVE estable (la que usa `orden_parcelas` del borrador). */
export interface ParcelaCuaderno {
  orden: number;
  /** «naveId|especie|variedad|inicio del ciclo», normalizado. No cambia al renumerar. */
  clave: string;
  naveId: string;
  nave: string | null;
  especie: string | null;
  variedad: string | null;
  inicio: string | null;
  forzada: boolean;
}

export interface CuadernoBuild {
  data: CuadernoDataInput;
  origenes: Record<string, Origen>;
  huecos: Hueco[];
  preguntas: Pregunta[];
  cobertura: ResultadoCuaderno["cobertura"];
  /** Hoja de revisión interna (NO se imprime en la copia oficial). */
  revision: HojaRevision;
  /** Meta de cada fila del 3.1, mismo índice que `data["3.1"]` (líneas y partes de origen). */
  meta31: MetaFila31[];
  periodo: ResultadoCuaderno["periodo"];
  parcelas: ParcelaCuaderno[];
  fuera: ResultadoCuaderno["fuera"];
  /** Líneas que el borrador quería quitar y no salieron. Desde CU9 (28-09-2026) siempre vacía. */
  noSePuedenQuitar: string[];
  borrador: DecisionesBorrador;
  /**
   * CU9: la entrada EFECTIVA con la que se montó el cuaderno: la de los partes más las
   * líneas añadidas por la persona (sintéticas). La guarda de salida recibe ESTA.
   */
  entrada: EntradaCuaderno;
}

type BorradorLike = Pick<BorradorCuaderno, "answers" | "line_overrides" | "version"> & Partial<Pick<BorradorCuaderno, "line_edits" | "added_lines">>;

/** El prefijo de los ids de parte y de línea que no vienen de ningún parte (CU9). */
export const PREFIJO_ANADIDA = "anadida:";

/**
 * Las líneas añadidas del borrador como partes y líneas sintéticas: una por nave de
 * las del cuaderno (una nave que ya no está, o de otro titular, no entra). El sector
 * es el de la nave con un ciclo activo ese día (para que el motor encuentre su
 * parcela y su cultivo) y el área, la de toda la nave. Sus campos van como
 * EDICIONES de la línea (clave = el id de la añadida).
 */
export function lineasAnadidas(e: EntradaCuaderno, nuevas: Record<string, LineaNueva>): { partes: ParteEntrada[]; lineas: LineaEntrada[]; ediciones: Record<string, EdicionLinea> } {
  const partes: ParteEntrada[] = [];
  const lineas: LineaEntrada[] = [];
  const ediciones: Record<string, EdicionLinea> = {};
  const naves = new Map(e.naves.map((n) => [n.id.toLowerCase(), n]));
  for (const [id, d] of Object.entries(nuevas)) {
    const { naves: navesDe, ...campos } = d;
    ediciones[id] = campos;
    for (const naveIdRaw of navesDe) {
      const nave = naves.get(naveIdRaw.toLowerCase());
      if (!nave) continue;
      const sectores = e.sectores.filter((s) => s.naveId === nave.id);
      const activo = sectores.find((s) => e.ciclos.some((c) => c.blockId === s.id && c.inicio <= d.fecha && (!c.fin || c.fin >= d.fecha)));
      const sector = activo ?? sectores.find((s) => s.cultivo) ?? sectores[0] ?? null;
      const areas = sectores.map((s) => Number(s.areaM2));
      const area = areas.length && areas.every((a) => Number.isFinite(a) && a > 0) ? areas.reduce((x, y) => x + y, 0) : null;
      const parteId = `${PREFIJO_ANADIDA}${id}:${nave.id}`;
      partes.push({ id: parteId, fecha: d.fecha, naveId: nave.id, blockId: sector?.id ?? null, areaM2: area, taskId: null });
      lineas.push({ id: parteId, parteId, nombre: d.producto.nombre, registro: d.producto.numero ?? null, anadida: true, claveEdicion: id });
    }
  }
  return { partes, lineas, ediciones };
}

/**
 * @param entrada lo que carga `loadCuadernoEntrada` (o un fixture).
 * @param draft   la fila de `cuaderno_drafts`, o null si todavía no hay borrador.
 * @param legal   los datos legales del titular; si no se pasa, los de `entrada.legal`.
 */
export function buildCuaderno(entrada: EntradaCuaderno, draft: BorradorLike | null, legal?: DatosLegalesRow | null): CuadernoBuild {
  const b = leerBorrador(draft);
  // CU9: las líneas añadidas por la persona, como partes y líneas sintéticas.
  const nuevas = lineasAnadidas(entrada, b.lineasNuevas);
  const e: EntradaCuaderno = {
    ...entrada,
    legal: legal !== undefined ? legal : entrada.legal,
    partes: nuevas.partes.length ? [...entrada.partes, ...nuevas.partes] : entrada.partes,
    lineas: nuevas.lineas.length ? [...entrada.lineas, ...nuevas.lineas] : entrada.lineas,
    decisiones: {
      ...(entrada.decisiones ?? {}),
      ...b.decisiones,
      excluidas: { ...(entrada.decisiones?.excluidas ?? {}), ...b.excluidas },
      aplica: { ...(entrada.decisiones?.aplica ?? {}), ...b.decisiones.aplica },
      ediciones: { ...(entrada.decisiones?.ediciones ?? {}), ...b.ediciones, ...nuevas.ediciones },
    },
  };
  const r = construirCuaderno(e);

  // Copia: el resultado del motor no se toca (la guarda lo vuelve a calcular).
  const data = JSON.parse(JSON.stringify(r.data)) as CuadernoDataInput;
  let origenes: Record<string, Origen> = { ...r.origenes };
  let huecos: Hueco[] = r.huecos.map((h) => ({ ...h }));
  let preguntas: Pregunta[] = r.preguntas.map((p) => ({ ...p }));

  // ── Fecha de apertura contestada ─────────────────────────────────────────
  if (b.fechaApertura && data["1.1"]) {
    data["1.1"] = { ...data["1.1"], fechaApertura: b.fechaApertura };
    origenes["1.1.fechaApertura"] = "usuario";
  }

  // ── Parcelas y nº de orden forzados ──────────────────────────────────────
  const plan = planDelMotor(e);
  const nombreNave = new Map(e.naves.map((n) => [n.id, n.nombre]));
  const mapa = renumerar(plan.map((p) => ({ orden: p.orden, clave: p.clave })), b.ordenForzado);
  const parcelas: ParcelaCuaderno[] = plan.map((p) => ({
    orden: mapa?.get(p.orden) ?? p.orden,
    clave: p.clave,
    naveId: p.naveId,
    nave: nombreNave.get(p.naveId) ?? null,
    especie: p.especie,
    variedad: p.variedad,
    inicio: p.inicio,
    forzada: b.ordenForzado[p.clave] != null,
  }));
  // Solo se renumera si el plan casa con el 2.1 del motor (mismos nº de orden).
  const ordenes21 = new Set((data["2.1"] ?? []).map((f) => f.orden));
  const casa = plan.length === ordenes21.size && plan.every((p) => ordenes21.has(p.orden));
  if (mapa && casa) {
    const forzadas = new Set(parcelas.filter((p) => p.forzada).map((p) => p.orden));
    ({ origenes, huecos } = aplicarRenumeracion(data, origenes, huecos, mapa, forzadas));
  }

  // ── Eficacia «Buena» por defecto, confirmada en bloque (D17) ─────────────
  // M1 (revisión de la ola 3): la confirmación tiene FECHA DE CORTE. Solo pasa a
  // «usuario» la fila cuyos partes se guardaron TODOS antes de confirmarla; un parte
  // registrado después (o sin fecha de guardado) sigue «por defecto» y se pregunta.
  if (b.eficaciaConfirmadaHasta) {
    const corte = Date.parse(b.eficaciaConfirmadaHasta);
    const creado = new Map(e.partes.map((p) => [p.id, p.creadoEn ? Date.parse(p.creadoEn) : NaN]));
    const cubierta = (m: (typeof r.meta31)[number]) =>
      m.partes.length > 0 && m.partes.every((id) => {
        const t = creado.get(id);
        return t !== undefined && Number.isFinite(t) && t <= corte;
      });
    r.meta31.forEach((m, i) => {
      const k = `3.1.${i}.eficacia`;
      if (origenes[k] === "defecto" && cubierta(m)) origenes[k] = "usuario";
    });
    const porConfirmar = r.meta31.filter(
      (m, i) => m.huecos.some((h) => h.campo === "eficacia") || origenes[`3.1.${i}.eficacia`] === "defecto",
    );
    preguntas = preguntas
      .map((p) =>
        p.clave === "eficacia"
          ? { ...p, cuenta: porConfirmar.length, partes: [...new Set(porConfirmar.flatMap((m) => m.partes))] }
          : p,
      )
      .filter((p) => p.clave !== "eficacia" || p.cuenta > 0);
  }

  const revision = hojaDeRevision({
    data,
    origenes,
    huecos,
    entrada: e,
    excluidas: b.excluidas,
    noSePuedenQuitar: r.noSePuedenQuitar,
    meta31: r.meta31,
  });

  return {
    data,
    origenes,
    huecos,
    preguntas,
    cobertura: r.cobertura,
    revision,
    meta31: r.meta31,
    periodo: r.periodo,
    parcelas: parcelas.sort((a, bb) => a.orden - bb.orden),
    fuera: r.fuera,
    noSePuedenQuitar: r.noSePuedenQuitar,
    borrador: b,
    entrada: e,
  };
}

/**
 * El mismo plan de parcelas que calcula el motor (mismo periodo y mismo filtro
 * de titular), para conocer la CLAVE de cada nº de orden. Si algún día el motor
 * filtra de otra forma, `buildCuaderno` lo nota (los nº no casan) y no renumera.
 */
function planDelMotor(e: EntradaCuaderno) {
  const periodo = e.campana
    ? { desde: e.campana.desde, hasta: e.campana.hasta }
    : { desde: `${e.anio}-01-01`, hasta: `${e.anio}-12-31` };
  const fincasIds = new Set(e.fincasDelTitular ?? e.fincas.map((f) => f.id));
  const fincas = e.fincas.filter((f) => fincasIds.has(f.id));
  const naves = e.naves.filter((n) => (e.fincasDelTitular ? n.locationId != null && fincasIds.has(n.locationId) : true));
  const navesIds = new Set(naves.map((n) => n.id));
  const sectores = e.sectores.filter((s) => navesIds.has(s.naveId));
  const naveDeSector = new Map(sectores.map((s) => [s.id, s.naveId]));
  const partes: ParteEntrada[] = e.partes.filter((p) => {
    if (p.fecha < periodo.desde || p.fecha > periodo.hasta) return false;
    const naveId = p.naveId ?? (p.blockId ? naveDeSector.get(p.blockId) : null);
    return !!naveId && navesIds.has(naveId);
  });
  return planDeParcelas({ periodo, fincas, naves, sectores, ciclos: e.ciclos, partes }).parcelas;
}

/**
 * Nº viejo → nº nuevo. Las forzadas llevan el suyo; las demás, en su orden de
 * siempre, los nº libres más bajos. null si no cambia nada.
 */
export function renumerar(parcelas: { orden: number; clave: string }[], forzado: Record<string, number>): Map<number, number> | null {
  const mapa = new Map<number, number>();
  const usados = new Set<number>();
  for (const p of parcelas) {
    const n = forzado[p.clave];
    if (n != null && !usados.has(n)) {
      mapa.set(p.orden, n);
      usados.add(n);
    }
  }
  if (!mapa.size) return null;
  let siguiente = 1;
  for (const p of [...parcelas].sort((a, b) => a.orden - b.orden)) {
    if (mapa.has(p.orden)) continue;
    while (usados.has(siguiente)) siguiente++;
    mapa.set(p.orden, siguiente);
    usados.add(siguiente);
  }
  return [...mapa].every(([a, b]) => a === b) ? null : mapa;
}

function remapRef(ref: ParcelasRef | null | undefined, mapa: Map<number, number>): ParcelasRef | null | undefined {
  if (!ref || ref === "TODAS" || !Array.isArray(ref)) return ref;
  return [...new Set(ref.map((n) => mapa.get(n) ?? n))].sort((a, b) => a - b);
}

/** Cambia los nº de orden en 2.1, 2.2, 3.1, 3.1 bis, 7 y la nota del 3.1; reordena el 2.1. */
function aplicarRenumeracion(
  data: CuadernoDataInput,
  origenes: Record<string, Origen>,
  huecos: Hueco[],
  mapa: Map<number, number>,
  forzadas: Set<number>,
): { origenes: Record<string, Origen>; huecos: Hueco[] } {
  const filas21 = (data["2.1"] ?? []).map((f, i) => ({ f: { ...f, orden: mapa.get(f.orden) ?? f.orden }, viejo: i }));
  filas21.sort((a, b) => a.f.orden - b.f.orden || a.viejo - b.viejo);
  const nuevoIndice = new Map(filas21.map((x, i) => [x.viejo, i]));
  data["2.1"] = filas21.map((x) => x.f);

  const orig: Record<string, Origen> = {};
  for (const [k, o] of Object.entries(origenes)) {
    const m = /^2\.1\.(\d+)(\..*)?$/.exec(k);
    if (m) orig[`2.1.${nuevoIndice.get(Number(m[1])) ?? m[1]}${m[2] ?? ""}`] = o;
    else orig[k] = o;
  }
  data["2.1"].forEach((f, i) => {
    if (forzadas.has(f.orden)) orig[`2.1.${i}.orden`] = "usuario";
  });
  const hs = huecos.map((h) => (h.seccion === "2.1" && h.fila != null ? { ...h, fila: nuevoIndice.get(h.fila) ?? h.fila } : h));

  data["2.2"] = (data["2.2"] ?? []).map((f) => ({ ...f, parcelas: remapRef(f.parcelas, mapa) as ParcelasRef }));
  data["3.1"] = (data["3.1"] ?? []).map((f) => ({ ...f, parcelas: remapRef(f.parcelas, mapa) as ParcelasRef }));
  if (data["3.1bis"]?.filas) {
    data["3.1bis"] = { ...data["3.1bis"], filas: data["3.1bis"].filas.map((f) => ({ ...f, parcelas: remapRef(f.parcelas, mapa) as ParcelasRef })) };
  }
  if (data["7"]?.aplicaciones) {
    data["7"] = { ...data["7"], aplicaciones: data["7"].aplicaciones.map((f) => ({ ...f, parcelas: remapRef(f.parcelas, mapa) as ParcelasRef })) };
  }
  const nota = data.notas?.["3.1"];
  if (nota) data.notas = { ...data.notas, "3.1": nota.replace(/\(parcela (\d+)\)/g, (_, n) => `(parcela ${mapa.get(Number(n)) ?? n})`) };
  return { origenes: orig, huecos: hs };
}
